import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Check, ChevronRight, X, Sparkles } from 'lucide-react';

/** Avvio guidato: resta finché il salone non ha finito di configurarsi. */
export const OnboardingCard = () => {
  const { API, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`, getAuthHeaders());
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async () => {
    setHidden(true);
    try {
      await axios.post(`${API}/onboarding/dismiss`, {}, getAuthHeaders());
    } catch { /* riapparirà al prossimo caricamento, nessun danno */ }
  };

  if (!data || hidden || data.dismissed || data.all_done) return null;

  const next = data.steps.find(s => !s.done);
  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <Card className="relative overflow-hidden border-primary/30" data-testid="onboarding-card">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
      <CardContent className="p-5 sm:p-6">
        <button
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Nascondi l'avvio guidato"
          data-testid="onboarding-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Sparkles className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold font-outfit md:text-lg" data-testid="onboarding-title">
              Prepariamo il salone
            </h2>
            <p className="text-xs text-muted-foreground" data-testid="onboarding-progress">
              {data.completed} di {data.total} passi completati
            </p>
          </div>
        </div>

        <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${pct}%` }}
            data-testid="onboarding-bar"
          />
        </div>

        <div className="mt-5 space-y-1">
          {data.steps.map((s) => {
            const isNext = next && s.id === next.id;
            return (
              <div
                key={s.id}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${isNext ? 'bg-card border border-primary/40' : ''}`}
                data-testid={`onboarding-step-${s.id}`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
                    s.done
                      ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                      : 'border-border text-muted-foreground'
                  }`}
                >
                  {s.done ? <Check className="h-3.5 w-3.5" /> : null}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium leading-snug ${s.done ? 'text-muted-foreground line-through' : ''}`}>
                    {s.title}
                  </p>
                  {isNext && (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{s.description}</p>
                  )}
                </div>
                {!s.done && (
                  <Link to={s.to} data-testid={`onboarding-go-${s.id}`}>
                    <Button size="sm" variant={isNext ? 'default' : 'ghost'} className="min-h-[36px] shrink-0">
                      <span className="hidden sm:inline">{s.action}</span>
                      <ChevronRight className="h-4 w-4 sm:ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
